// @flow
import React from "react";
import cx from "classnames";
import glamorous from "glamorous";
import Downshift from "downshift";
import { isEmpty } from "lodash";
import { Manager, Reference, Popper } from "react-popper";
import SvgIcon from "root/components/SvgIcon";
import ICONS from "~/assets/icons/icons";

type DropdownProps = {
	width?: number | string,
	className?: string,
	placement?: "top" | "bottom",
	selectedItem?: any | { value: string | number, label: string },
	items?: any[] | Array<{ value: string | number, label: string }>,
	renderItem?: Function,
	renderPlaceholder?: Function,
	renderInputValue?: Function,
	onChange?: Function,
	disabled?: boolean
};

const Wrap = glamorous.button(
	"w-100 flex items-center bg-window tl ba bc-window-border",
	({
		 isOpen,
		 placement,
	 }: {
		isOpen: boolean,
		placement: "top" | "bottom"
	}) => ({
		borderRadius: isOpen
			? placement === "bottom"
				? "5px 5px 0 0"
				: "0 0 5px 5px"
			: "5px",
		padding: "7px 10px",
		outline: "none",
	}),
);

const InputValue = glamorous.div("truncate text", {
	boxSizing: "border-box",
	flexGrow: 1,
	backgroundColor: "transparent",
	fontSize: 12,
});

const Placeholder = glamorous(InputValue)("c-window-border");

const ResultsWrap = glamorous.div(
	"f6 bb bl br bc-window-border pointer bg-window absolute w-100 options-wrap",
	({ placement }: { placement: "top" | "bottom" }) => ({
		borderRadius: placement === "bottom" ? "0 0 5px 5px" : "5px 5px 0 0",
		borderWidth: placement === "bottom" ? "0 1px 1px 1px" : "1px 1px 0 1px",
		borderStyle: "solid",
		boxShadow: "0 2px 3px 0 rgba(0,0,0,.15)",
		overflowY: "auto",
		maxHeight: 200,
		bottom: placement === "bottom" ? "initial" : "100%",
		zIndex: 2,
	}),
);

const Item = glamorous.div("truncate db bn f6 tl bg-window-c option", {
	padding: "7px 10px",
	boxSizing: "border-box",
	wordWrap: "normal",
	minHeight: "29px"
});

const Arrow = ({ isOpen }: { isOpen: boolean }) => (
	<SvgIcon
		style={{ marginLeft: 10, cursor: "pointer", flex: "none" }}
		path={ICONS[isOpen ? "ARROW_DROP_UP" : "ARROW_DROP_DOWN"]}
		size={16}
	/>
);

const itemToString = (item) => (item ? (item.label ? item.label.toString() : item.toString()) : "");

class DropdownList extends React.Component<DropdownProps> {
	static defaultProps = {
		placement: "bottom",
	};

	render() {
		const {
			items = [],
			width = "100%",
			selectedItem,
			placement,
			onChange = () => {
			},
			renderInputValue = (item) => (item && item.label) || item,
			renderPlaceholder = (placeholder) => placeholder,
			renderItem = (item) => item.label || item,
			className,
			disabled,
			dataTest = undefined,
		} = this.props;

		return (
			<Manager>
				<Downshift
					defaultSelectedItem={selectedItem}
					onChange={onChange}
					itemToString={itemToString}
					render={({
								 getButtonProps,
								 getItemProps,
								 isOpen,
								 highlightedIndex,
							 }) => (
						<div className={cx("w-100 relative", className)} data-test={dataTest}>
							<Reference>
								{({ ref }) => (
									<Wrap
										ref={ref}
										isOpen={isOpen}
										placement={placement}
										width={width}
										{...getButtonProps({ disabled })}
									>
										{isEmpty(selectedItem) ? (
											<Placeholder>
												{renderPlaceholder()}
											</Placeholder>
										) : (
											<InputValue>
												{renderInputValue(selectedItem)}
											</InputValue>
										)}
										<Arrow isOpen={isOpen}/>
									</Wrap>
								)}
							</Reference>
							{isOpen && (
								<Popper>
									{() => (
										<ResultsWrap placement={placement}>
											{items.map((item, index) => (
												<Item
													key={item.value || item}
													{...getItemProps({
														item,
														disabled,
														className: cx({
															"bg-source-bg bg-source-bg-c":
																highlightedIndex
																=== index
																&& selectedItem
																!== item,
															"bg-link bg-link-c":
																selectedItem
																=== item
																|| (selectedItem
																&& selectedItem.value
																&& selectedItem.value
																=== item.value),
														}),
													})}
												>
													{renderItem(item)}
												</Item>
											))}
										</ResultsWrap>
									)}
								</Popper>
							)}
						</div>
					)}
				/>
			</Manager>
		);
	}
}

export default DropdownList;
